import React from "react";

import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useFirebase from "../../config/context/firebaseContext";
import useMainState from "../../config/context/mainStateContext";
import useModal from "../../config/context/modalContext";
import { MainIndexDB } from "../../config/mainIndexDB";
import storageKey from "../../config/storageKey";
import { Dev } from "../../config/util/consoleUtil";
import { UtilComponents } from "../../config/util/utilComponents";
import UtilFunctions from "../../config/util/utilFunctions";

import MainBannerItem from "./components/MainBannerItem";

import { BannerItemType, BannerScreensAvailableType } from "../../types/common";

type MainConfigBannerProps = { screen: BannerScreensAvailableType };

export default function MainConfigBanner({ screen }: MainConfigBannerProps) {
  const navigate = useNavigate();

  const { getConfig } = useFirebase();
  const { openLink } = useMainState();
  const { pushAlert, showImage } = useModal();

  const [bannerDimiss, setBannerDimiss] = React.useState<string[]>([]);

  const [bannerList, setBannerList] = React.useState<BannerItemType[]>([]);

  const handleFetchBanners = async () => {
    try {
      const storageDimissed = await JSON.parse(
        window.localStorage.getItem(storageKey.DIMISSED_BANNERS) || "[]"
      );

      setBannerDimiss(storageDimissed);

      const bannerListConfig = getConfig("banner_list") as any;

      if (bannerListConfig?.length) {
        setBannerList(UtilFunctions.bannerFiltering(bannerListConfig, storageDimissed, screen));
      }
    } catch (error) {
      console.log(...new Dev("handleFetchBanners", error).Error());
    }
  };

  const handleDimiss = (bannerItem: BannerItemType) => {
    try {
      const newState = [...bannerDimiss, bannerItem.id];
      setBannerDimiss(newState);
      if (!bannerItem.persist)
        window.localStorage.setItem(storageKey.DIMISSED_BANNERS, JSON.stringify(newState));
    } catch (error) {
      console.log(...new Dev("handleDimiss", error).Error());
    }
  };

  const handleDownloadAndShowImage = async (bannerData: BannerItemType) => {
    const defaultImageStatintString = "https://storage.googleapis.com/circular-ufrn.appspot.com/";
    const bannerUrl = bannerData.action?.content;
    const bannerId = bannerData.id;

    if (!bannerUrl) return;

    if (!bannerUrl.startsWith(defaultImageStatintString)) {
      pushAlert({ type: "error", message: "Não foi possível abrir o link fornecido" });
      return;
    }

    if (bannerData.action?.meta?.cache || "a") {
      const preFix = "banner_image-";
      const fileNameToCache = preFix + bannerId;
      const dataRetrieved = await MainIndexDB.retrieveAllStore("file-objects");
      const filesRetrieved: File[] = dataRetrieved.map((item) => item.value.content) as File[];

      let currentBannerItem = filesRetrieved.find((file) => file.name === fileNameToCache);

      await MainIndexDB.deleteItemsDB("file-objects", [
        {
          key: "name",
          value: preFix,
          comparison: "includes",
        },
        {
          key: "lastModified",
          value: DateTime.now().minus({ days: 4 }).toMillis(),
          comparison: "<",
        },
      ]);

      if (currentBannerItem) {
        const imageData = await UtilFunctions.convertFileToBase64(currentBannerItem);
        showImage(imageData);
      } else {
        const blobToIndexedDbJson = async (blobData: Blob) => {
          let fileObject = new File([blobData as Blob], fileNameToCache);

          await MainIndexDB.saveDataToStore("file-objects", { content: fileObject });
          const imageData = await UtilFunctions.convertFileToBase64(fileObject);
          showImage(imageData);
          return;
        };

        await fetch(bannerUrl)
          .then(async (response) => {
            let blobData = await response.blob();

            return blobToIndexedDbJson(blobData as Blob);
          })
          .catch((err) => {
            console.log(...new Dev("handleDownloadAndShowImage", err).Error());

            showImage(bannerUrl);

            pushAlert({
              type: "error",
              message: "Erro na requisição. Possivelmente CORS. A imagem não será armazenada.",
            });
          });
      }
    } else {
      showImage(bannerUrl);
    }

    return;
  };

  const handleCopyToClipboard = (content: string) => {
    UtilComponents.copyToClipboard(pushAlert, content, "Copiado para área de transferência!");
  };

  React.useEffect(() => {
    handleFetchBanners();
  }, []);

  return (
    <Box mb={bannerList.length > 0 ? 2 : 0}>
      {bannerList.map((item) => (
        <MainBannerItem
          key={item.id}
          item={item}
          collapseIn={!bannerDimiss.includes(item.id)}
          onDimiss={() => handleDimiss(item)}
          onActionClick={() => {
            item?.action?.type === "external-link"
              ? openLink(String(item?.action?.content))
              : item?.action?.type === "image"
              ? handleDownloadAndShowImage(item)
              : item?.action?.type === "navigation"
              ? navigate(String(item?.action?.content))
              : item?.action?.type === "clipboard"
              ? handleCopyToClipboard(item?.action?.content)
              : pushAlert({ type: "error", message: "Ação desconhecida" });
          }}
        />
      ))}
    </Box>
  );
}
